import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../hooks/useAuth';
import { usePresence } from '../hooks/usePresence';
import { track } from '../lib/analytics';
import { setSignInOpen, signOut } from '../lib/auth';
import { strings } from '../lib/strings';
import { Avatar } from './Avatar';
import { SignOutIcon } from './Icons';
import { RollLabel } from './RollLabel';

const EXIT_MS = 180;

export function AccountButton({ tone }: { tone: 'dark' | 'light' }) {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const { mounted, shown } = usePresence(open, EXIT_MS);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const signOutRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const dark = tone === 'dark';

  useEffect(() => {
    if (!session) setOpen(false);
  }, [session]);

  useEffect(() => {
    if (open && mounted) signOutRef.current?.focus({ preventScroll: true });
  }, [open, mounted]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !buttonRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!session) {
    const signInButton = (
      <button
        type="button"
        onClick={() => setSignInOpen(true)}
        className={`cta pressable flex h-[34px] shrink-0 items-center rounded-full px-4 text-sm font-semibold ${
          dark ? 'bg-white text-ink [--cta-fill:var(--color-primary)]' : 'bg-primary text-white'
        }`}
      >
        <RollLabel>{strings.signIn}</RollLabel>
      </button>
    );
    return (
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => setSignInOpen(true, 'register')}
          className={`pressable hidden h-[34px] shrink-0 items-center rounded-full px-3 text-sm font-semibold sm:flex ${
            dark ? 'text-white/85 hover:bg-white/10 hover:text-white' : 'text-ink hover:bg-fill'
          }`}
        >
          {strings.createAccount}
        </button>
        {signInButton}
      </div>
    );
  }

  const { profile } = session;
  const secondary = [profile.title, profile.customerName].filter(Boolean).join(' · ');

  return (
    <div className="relative shrink-0">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`${strings.account}: ${profile.name}`}
        title={strings.signedInAs(profile.name)}
        data-cursor-stick
        onClick={() => setOpen((v) => !v)}
        className={`pressable flex h-[34px] w-[34px] items-center justify-center rounded-full ${
          dark ? 'ring-2 ring-white/20 hover:ring-white/40' : 'ring-2 ring-transparent hover:ring-fill-strong'
        } ${open ? (dark ? 'ring-white/50' : 'ring-fill-strong') : ''}`}
      >
        <Avatar profile={profile} size={32} />
      </button>
      {mounted && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={strings.account}
          className="absolute right-0 top-full z-popover mt-2 w-[272px] max-w-[calc(100vw-32px)] origin-top-right overflow-hidden rounded-md bg-material-thick shadow-overlay ring-1 ring-black/5 backdrop-blur-2xl backdrop-saturate-150"
          style={{
            opacity: shown ? 1 : 0,
            transform: shown ? 'none' : 'scale(0.72) translateY(-6px)',
            transition: shown
              ? 'transform 460ms var(--ease-spring), opacity 180ms var(--ease-standard)'
              : `transform ${EXIT_MS}ms var(--ease-standard), opacity ${EXIT_MS}ms var(--ease-standard)`,
          }}
        >
          <div className="flex items-center gap-3 px-4 py-3">
            <Avatar profile={profile} size={44} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-base font-semibold tracking-tight text-ink">{profile.name}</div>
              {(profile.email ?? profile.userName) && (
                <div className="truncate text-sm text-muted">{profile.email ?? profile.userName}</div>
              )}
              {secondary && <div className="truncate text-xs text-subtle">{secondary}</div>}
            </div>
          </div>
          {session.canUseStudio && (
            <Link
              to="/studio"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="relative flex min-h-[44px] w-full items-center px-4 text-left text-base text-ink transition-colors duration-fast before:absolute before:left-0 before:right-0 before:top-0 before:h-px before:bg-line hover:bg-fill focus-visible:bg-fill focus-visible:shadow-none"
            >
              {strings.studio.menu}
            </Link>
          )}
          <button
            ref={signOutRef}
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              track('logout', { sign_in_method: session.method });
              void signOut();
            }}
            className="relative flex min-h-[44px] w-full items-center justify-between gap-2 px-4 text-left text-base text-danger transition-colors duration-fast before:absolute before:left-0 before:right-0 before:top-0 before:h-px before:bg-line hover:bg-fill focus-visible:bg-fill focus-visible:shadow-none"
          >
            {strings.signOut}
            <SignOutIcon className="h-[18px] w-[18px]" />
          </button>
        </div>
      )}
    </div>
  );
}
